import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import ErrorMessageComponent from '../components/ErrorMessage';
import InventoryAdjustmentModal from '../components/InventoryAdjustmentModal';
import InventoryStatsCards, { InventoryStats } from '../components/InventoryStatsCards';
import LoadingSpinner from '../components/LoadingSpinner';
import ResponsiveTable from '../components/ResponsiveTable';
import { EmptyState, FilterPanel } from '../components/ui';
import api from '../services/api';
import { ExcelColumn, exportToExcel } from '../utils/exportToExcel';

const LOW_STOCK_THRESHOLD = 50;
const OUT_OF_STOCK = 0;

export interface InventoryItem {
  id: number;
  wineName?: string;
  wine_name?: string;
  wineType?: string;
  wine_type?: string;
  grapeVariety?: string;
  grape_variety?: string;
  vintage?: number;
  bottleSize?: string;
  bottle_size?: string;
  quantity: number;
  vineyardId?: number;
  vineyard_id?: number;
  vesselId?: number;
  vessel_id?: number;
  location?: string;
  pricePerBottle?: number;
  price_per_bottle?: number;
  bottlingId?: number;
  bottling_id?: number;
  externalSourceId?: number;
  external_source_id?: number;
  vineyard?: { id: number; name?: string };
  vessel?: { id: number; name?: string; type?: string };
  bottling?: { bottling_date?: string; bottlingDate?: string };
}

interface FilterState {
  wineType?: string;
  vineyardId?: string;
  vesselId?: string;
  vintageMin?: string;
  vintageMax?: string;
  stockStatus?: 'all' | 'in_stock' | 'low_stock' | 'out_of_stock';
}

const Inventory: React.FC<{ tabVisible?: boolean }> = ({ tabVisible = true }) => {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [stats, setStats] = useState<InventoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [filters, setFilters] = useState<FilterState>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'cards' : 'table'
  );
  const [adjustModal, setAdjustModal] = useState<{ open: boolean; item: InventoryItem | null }>({ open: false, item: null });
  const [vineyards, setVineyards] = useState<{ id: number; name: string }[]>([]);
  const [vessels, setVessels] = useState<{ id: number; name?: string; type?: string }[]>([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string | number> = {};
      if (filters.wineType) params.wineType = filters.wineType;
      if (filters.vineyardId) params.vineyardId = filters.vineyardId;
      if (filters.vesselId) params.vesselId = filters.vesselId;
      if (filters.vintageMin) params.vintageMin = filters.vintageMin;
      if (filters.vintageMax) params.vintageMax = filters.vintageMax;
      if (filters.stockStatus && filters.stockStatus !== 'all') params.stockStatus = filters.stockStatus;

      const [invRes, statsRes, vineyardsRes, vesselsRes] = await Promise.all([
        api.get('/api/inventory', { params }),
        api.get('/api/inventory/stats').catch(() => ({ data: null })),
        api.get('/api/vineyard').catch(() => ({ data: [] })),
        api.get('/api/vessel').catch(() => ({ data: [] }))
      ]);

      setInventory(Array.isArray(invRes.data) ? invRes.data : []);
      setStats(statsRes?.data ?? null);
      setVineyards(Array.isArray(vineyardsRes?.data) ? vineyardsRes.data : []);
      setVessels(Array.isArray(vesselsRes?.data) ? vesselsRes.data : []);
    } catch (err: any) {
      setError(err);
      setInventory([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [filters.wineType, filters.vineyardId, filters.vesselId, filters.vintageMin, filters.vintageMax, filters.stockStatus]);

  useEffect(() => {
    if (tabVisible) fetchData();
  }, [fetchData, tabVisible]);

  const filteredBySearch = useMemo(() => {
    if (!searchQuery.trim()) return inventory;
    const q = searchQuery.toLowerCase();
    return inventory.filter(
      (item) =>
        (item.wineName ?? item.wine_name ?? '').toLowerCase().includes(q) ||
        (item.grapeVariety ?? item.grape_variety ?? '').toLowerCase().includes(q)
    );
  }, [inventory, searchQuery]);

  const getStockStatus = (qty: number) => {
    if (qty <= OUT_OF_STOCK) return { label: 'Out of Stock', color: 'bg-error-500 text-white' };
    if (qty <= LOW_STOCK_THRESHOLD) return { label: 'Low Stock', color: 'bg-warning-500 text-white' };
    return { label: 'In Stock', color: 'bg-success-500 text-white' };
  };

  const getSource = (item: InventoryItem) => {
    if (item.externalSourceId ?? item.external_source_id) return 'External';
    const b = item.bottling;
    if (b?.bottling_date) return new Date(b.bottling_date).toLocaleDateString();
    if (b?.bottlingDate) return new Date(b.bottlingDate).toLocaleDateString();
    return '–';
  };

  const columns = [
    {
      key: 'wineName',
      label: 'Lot Number',
      render: (_: unknown, row: InventoryItem) => row.wineName ?? row.wine_name ?? '–'
    },
    {
      key: 'wineType',
      label: 'Type',
      render: (_: unknown, row: InventoryItem) => {
        const t = row.wineType ?? row.wine_type ?? '–';
        return <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">{t}</span>;
      }
    },
    { key: 'variety', label: 'Variety', render: (_: unknown, row: InventoryItem) => row.grapeVariety ?? row.grape_variety ?? '–' },
    { key: 'vintage', label: 'Vintage', render: (_: unknown, row: InventoryItem) => row.vintage ?? '–' },
    { key: 'bottleSize', label: 'Size', render: (_: unknown, row: InventoryItem) => row.bottleSize ?? row.bottle_size ?? '–' },
    {
      key: 'quantity',
      label: 'Quantity',
      render: (_: unknown, row: InventoryItem) => {
        const qty = row.quantity ?? 0;
        const status = getStockStatus(qty);
        return (
          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${status.color}`}>
            {qty} ({status.label})
          </span>
        );
      }
    },
    { key: 'source', label: 'Source', render: (_: unknown, row: InventoryItem) => getSource(row) },
    { key: 'location', label: 'Location', render: (_: unknown, row: InventoryItem) => row.location ?? '–' },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: unknown, row: InventoryItem) => (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setAdjustModal({ open: true, item: row }); }}
            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-sm font-medium text-primary-600 hover:text-primary-700 md:min-h-0 md:min-w-0"
          >
            Adjust
          </button>
        </div>
      )
    }
  ];

  const exportColumns: ExcelColumn<InventoryItem>[] = [
    { header: 'Lot Number', accessor: (r) => r.wineName ?? r.wine_name ?? '' },
    { header: 'Type', accessor: (r) => r.wineType ?? r.wine_type ?? '' },
    { header: 'Variety', accessor: (r) => r.grapeVariety ?? r.grape_variety ?? '' },
    { header: 'Vintage', accessor: (r) => r.vintage ?? '' },
    { header: 'Bottle Size', accessor: (r) => r.bottleSize ?? r.bottle_size ?? '' },
    { header: 'Quantity', accessor: (r) => r.quantity },
    { header: 'Source', accessor: (r) => getSource(r) },
    { header: 'Location', accessor: (r) => r.location ?? '' }
  ];

  const handleExport = () => {
    exportToExcel({
      fileName: `inventory-export-${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: 'Inventory',
      rows: filteredBySearch,
      columns: exportColumns
    });
  };

  const applyLowStockFilter = () => {
    setFilters((prev) => ({ ...prev, stockStatus: 'low_stock' }));
  };

  if (loading && inventory.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {error && <ErrorMessageComponent error={error} className="mb-4" />}

      <InventoryStatsCards
        stats={stats}
        loading={loading}
        onLowStockClick={applyLowStockFilter}
      />

      <div className="flex flex-col gap-4">
        <FilterPanel title="Filters" collapsible defaultExpanded className="w-full max-w-full">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
            <div>
              <label className="block text-xs font-medium text-monday-text-muted mb-1">Wine Type</label>
              <select
                value={filters.wineType ?? ''}
                onChange={(e) => setFilters((p) => ({ ...p, wineType: e.target.value || undefined }))}
                className="w-full rounded border border-monday-border px-2 py-1.5 text-sm"
              >
                <option value="">All</option>
                <option value="Red">Red</option>
                <option value="White">White</option>
                <option value="Rosé">Rosé</option>
                <option value="Sparkling">Sparkling</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-monday-text-muted mb-1">Vineyard</label>
              <select
                value={filters.vineyardId ?? ''}
                onChange={(e) => setFilters((p) => ({ ...p, vineyardId: e.target.value || undefined }))}
                className="w-full rounded border border-monday-border px-2 py-1.5 text-sm"
              >
                <option value="">All</option>
                {vineyards.map((v) => (
                  <option key={v.id} value={String(v.id)}>{v.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-monday-text-muted mb-1">Vessel</label>
              <select
                value={filters.vesselId ?? ''}
                onChange={(e) => setFilters((p) => ({ ...p, vesselId: e.target.value || undefined }))}
                className="w-full rounded border border-monday-border px-2 py-1.5 text-sm"
              >
                <option value="">All</option>
                {vessels.map((v) => (
                  <option key={v.id} value={String(v.id)}>{v.name ?? v.type ?? `Vessel ${v.id}`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-monday-text-muted mb-1">Stock Status</label>
              <select
                value={filters.stockStatus ?? 'all'}
                onChange={(e) => setFilters((p) => ({ ...p, stockStatus: e.target.value as FilterState['stockStatus'] }))}
                className="w-full rounded border border-monday-border px-2 py-1.5 text-sm"
              >
                <option value="all">All</option>
                <option value="in_stock">In Stock</option>
                <option value="low_stock">Low Stock</option>
                <option value="out_of_stock">Out of Stock</option>
              </select>
            </div>
            <button
              type="button"
              onClick={() => setFilters({})}
              className="text-sm text-primary-600 hover:underline"
            >
              Clear filters
            </button>
          </div>
        </FilterPanel>

        <div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <input
              type="search"
              placeholder="Search wine or variety..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 min-w-[200px] rounded-lg border border-monday-border px-3 py-2 text-sm"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`p-2 rounded ${viewMode === 'table' ? 'bg-primary-100 text-primary-700' : 'text-monday-text-muted hover:bg-monday-header'}`}
                aria-label="Table view"
              >
                {FiIcons.FiGrid({ size: 18 })}
              </button>
              <button
                type="button"
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded ${viewMode === 'cards' ? 'bg-primary-100 text-primary-700' : 'text-monday-text-muted hover:bg-monday-header'}`}
                aria-label="Card view"
              >
                {FiIcons.FiLayers({ size: 18 })}
              </button>
              <button
                type="button"
                onClick={handleExport}
                disabled={filteredBySearch.length === 0}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded border border-monday-border bg-white text-monday-text hover:bg-monday-header disabled:opacity-50"
              >
                {FiIcons.FiDownload({ size: 16 })} Export
              </button>
            </div>
          </div>

          {viewMode === 'table' ? (
            <ResponsiveTable
              columns={columns}
              data={filteredBySearch}
              emptyMessage="No inventory matches your filters."
              loading={loading}
              enableExport
              exportFileName="inventory"
              compact
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBySearch.length === 0 ? (
                <div className="col-span-full">
                  <EmptyState
                    title="No inventory"
                    message="No bottles match your filters. Try adjusting filters or search."
                  />
                </div>
              ) : (
                filteredBySearch.map((item) => {
                  const status = getStockStatus(item.quantity ?? 0);
                  return (
                    <div
                      key={item.id}
                      className="bg-white rounded-lg border border-monday-border-light shadow p-4"
                    >
                      <div className="font-medium text-monday-text">
                        {item.wineName ?? item.wine_name ?? `Item #${item.id}`}
                      </div>
                      <div className="text-sm text-monday-text-muted mt-1">
                        {item.wineType ?? item.wine_type} · {item.bottleSize ?? item.bottle_size} · {item.vintage ?? '–'}
                      </div>
                      <div className="mt-2">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${status.color}`}>
                          {item.quantity} – {status.label}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAdjustModal({ open: true, item })}
                        className="mt-3 min-h-[44px] inline-flex items-center text-sm font-medium text-primary-600 hover:underline"
                      >
                        Adjust quantity
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      <InventoryAdjustmentModal
        isOpen={adjustModal.open}
        onClose={() => setAdjustModal({ open: false, item: null })}
        item={adjustModal.item ? { id: adjustModal.item.id, wineName: adjustModal.item.wineName ?? adjustModal.item.wine_name, quantity: adjustModal.item.quantity } : null}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default Inventory;
